import React, { useContext } from 'react';
import NumberFormat from 'react-number-format';
import styled, { ThemeContext } from 'styled-components';

import Controller from '../Controller/Controller';
import Affix from '../Affix/Affix';

import DateFormat from '../DateFormat/DateFormat';
import { NewInputProps } from '../../NewInput/NewInput';
import { getMask } from './getMask';
import { unmask } from '../../../helpers';
import useInkAnalytics from '../../../hooks/useInkAnalytics';
import ErrorContext from '../Controller/errorContext';

/* @internal */
export const InputElement = styled.input`
  ${Controller.styles.base};
  ${Controller.styles.textEntry};
`;

/* @internal */
export const StyledDateFormat = styled(DateFormat)`
  ${Controller.styles.base};
  ${Controller.styles.textEntry};
  ${Controller.styles.numericEntry};

  // Prevents NewDatePicker input focus from clipping
  &:focus-visible {
    z-index: 1;
  }
`;

/* @internal */
export const StyledNumberFormat = styled(NumberFormat)`
  ${Controller.styles.base};
  ${Controller.styles.textEntry};
  ${Controller.styles.numericEntry};
`;

const inputSelector = (mask, props) => {
  if (['date', 'short-date'].includes(mask)) {
    return <StyledDateFormat {...getMask(mask)} {...props} />;
  }

  if (mask) {
    return <StyledNumberFormat {...getMask(mask, props)} {...props} />;
  }

  return <InputElement {...props} />;
};

function CoreInput(props: NewInputProps) {
  const { externalRef, inputRef, inputType, ...otherProps } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef || inputRef });
  const themeContext = useContext(ThemeContext);

  if (inputRef) {
    console.warn(
      '[INK DEPRECATION WARNING]: The NewInput `inputRef` prop is deprecated and will be removed. Use the `externalRef` prop instead.',
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) {
      props.onChange(e, {
        unmaskedValue: unmask(e.target.value),
      });
    }
  };

  /* We realized that many html input types have visual styles that conflict with our own, so we're allowing these cases and we will examine each other type use case individually as it comes up */
  const allowedTypes = ['text', 'password', 'number', 'tel', 'email', 'search'];
  const internalInputType = inputType && allowedTypes.includes(inputType) ? inputType : 'text';
  const errorContext = useContext(ErrorContext);

  return (
    <Controller
      {...otherProps}
      render={controllerProps => {
        const { mask, prefix, suffix, render, invalid, inline, onChange, className, ...otherProps } = controllerProps;

        const inputProps = {
          ...otherProps,
          ref,
          theme: {
            ...themeContext,
            ...controllerProps.theme,
            prefix: !!prefix,
            suffix: !!suffix,
          },
          onChange: handleChange,
          key: '1',
          placeholder: props.placeholder || ' ',
          'aria-required': themeContext?.fieldRequired,
          'aria-errormessage':
            errorContext.hasError && errorContext.errorAutoInvalidate ? errorContext.errorMessageId : undefined,
          'aria-invalid': invalid || (errorContext.hasError && errorContext.errorAutoInvalidate),
          type: internalInputType,
        };

        const input = inputSelector(mask, inputProps);

        return (
          <Controller.Wrapper className={className} theme={inputProps.theme}>
            {prefix &&
              (typeof prefix === 'function' ? (
                prefix()
              ) : (
                <Affix
                  theme={{
                    ...themeContext,
                    ...controllerProps.theme,
                    seam: 'right',
                  }}
                >
                  {prefix}
                </Affix>
              ))}

            {input}

            {suffix &&
              (typeof suffix === 'function' ? (
                suffix(errorContext.hasError && errorContext.errorAutoInvalidate)
              ) : (
                <Affix
                  theme={{
                    ...themeContext,
                    ...controllerProps.theme,
                    seam: 'left',
                  }}
                >
                  {suffix}
                </Affix>
              ))}
          </Controller.Wrapper>
        );
      }}
    />
  );
}

CoreInput.defaultProps = {
  value: undefined,
  onChange: undefined,
  width: 'large',
  align: 'left',
  prefix: undefined,
  suffix: undefined,
  invalid: false,
  inline: false,
  mask: undefined,
  inputType: 'text',
};

CoreInput.displayName = 'CoreInput';

export default CoreInput;
